import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    ChatCircleDots,
    PaperPlaneRight,
    CheckCircle,
    Clock,
    ArrowsClockwise,
    Gift,
} from "@phosphor-icons/react";
import { submitInquiry, fetchServices } from "@/lib/api";

const NEED_OPTIONS = [
    { value: "one_time", label: "One-time project", icon: Clock, color: "cyan",
      activeClasses: "border-cyan-400/70 bg-cyan-500/15 text-cyan-100",
      iconActive: "text-cyan-300" },
    { value: "regular", label: "Regular / ongoing basis", icon: ArrowsClockwise, color: "violet",
      activeClasses: "border-violet-400/70 bg-violet-500/15 text-violet-100",
      iconActive: "text-violet-300" },
    { value: "free_trial", label: "Start with a free trial", icon: Gift, color: "orange",
      activeClasses: "border-orange-400/70 bg-orange-500/15 text-orange-100",
      iconActive: "text-orange-300" },
];

const SURVEY_TYPES = [
    "Boundary Survey",
    "Topographic Survey",
    "ALTA/NSPS Land Title Survey",
    "As-Built Survey",
    "Subdivision Plat",
    "Construction Staking",
    "Route / Corridor Survey",
    "FEMA Elevation Certificate",
    "Site Plan",
    "Volumetric / Earthwork",
    "Wetland Delineation",
    "3D Laser Scanning / Point Cloud to CAD",
    "Not sure — recommend for me",
];

const DEFAULT_HEADLINES = {
    "/": {
        eyebrow: "Quick Inquiry",
        title: "Tell us what you need.",
        subtitle: "Pick the services you're exploring — we'll send a tailored proposal to surveys2cad@gmail.com within 1 business hour.",
    },
    "/services": {
        eyebrow: "Found the right service?",
        title: "Let's scope your drawing.",
        subtitle: "Choose everything you need drafted. We reply with deliverables, turnaround, and fixed pricing.",
    },
    "/about": {
        eyebrow: "Work with our team",
        title: "42+ specialists — ready for your project.",
        subtitle: "Quick intro — tell us the type of work and whether it's one-off or ongoing.",
    },
    "/contact": {
        eyebrow: "Prefer the fast lane?",
        title: "Skip the form — fire off a quick brief.",
        subtitle: "Covers 80% of what we need to quote. We'll follow up with the remaining 20%.",
    },
    "/get-started": {
        eyebrow: "Or — the 60-second version",
        title: "No time for the full wizard?",
        subtitle: "Drop the essentials here and we'll take it from there.",
    },
    "/blog": {
        eyebrow: "Liked an article?",
        title: "Let's apply it to your project.",
        subtitle: "Tell us what you're working on — we'll match the right drafter and turnaround.",
    },
};

export default function QuickInquiry({ headlineOverride = null }) {
    const loc = useLocation();
    const headline =
        headlineOverride ||
        DEFAULT_HEADLINES[loc.pathname] ||
        (loc.pathname.startsWith("/services/")
            ? {
                  eyebrow: "Ready to start?",
                  title: "Brief us on this specific service.",
                  subtitle: "We'll reply with a turnaround estimate and fixed-price quote.",
              }
            : DEFAULT_HEADLINES["/"]);

    const [services, setServices] = useState([]);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        company: "",
        services_looking_for: [],
        survey_type: "",
        need_type: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        fetchServices()
            .then((s) => setServices(s || []))
            .catch(() => {});
    }, []);

    const toggleService = (name) => {
        setForm((f) => ({
            ...f,
            services_looking_for: f.services_looking_for.includes(name)
                ? f.services_looking_for.filter((x) => x !== name)
                : [...f.services_looking_for, name],
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.need_type) {
            toast.error("Please pick how often you'll need us (one-time / regular / free trial).");
            return;
        }
        setLoading(true);
        try {
            await submitInquiry({ ...form, page: loc.pathname });
            setSent(true);
            toast.success("Got it — we just emailed our team. Expect a reply within 1 business hour.");
        } catch {
            toast.error("Couldn't send. Please try again or use the contact page.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            className="bg-section-midnight section-divider relative overflow-hidden"
            data-testid="quick-inquiry-section"
        >
            {/* Decorative background flourish */}
            <div
                className="absolute inset-0 pointer-events-none opacity-40"
                style={{
                    background:
                        "radial-gradient(600px 300px at 80% 20%, rgba(34,211,238,0.08), transparent 60%)," +
                        "radial-gradient(500px 300px at 10% 80%, rgba(251,146,60,0.07), transparent 60%)",
                }}
            />
            <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 md:py-24 relative grid grid-cols-1 lg:grid-cols-5 gap-10">
                <div className="lg:col-span-2">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/5 mb-5">
                        <ChatCircleDots size={14} weight="fill" className="text-cyan-400" />
                        <span className="overline !text-cyan-300 !text-[10px]">{headline.eyebrow}</span>
                    </div>
                    <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-50 tracking-tighter leading-[1.05]">
                        {headline.title}
                    </h2>
                    <p className="text-slate-400 mt-5 leading-relaxed max-w-md">
                        {headline.subtitle}
                    </p>
                    <div className="mt-8 space-y-3">
                        {[
                            { label: "Reply within 1 business hour", c: "text-cyan-400" },
                            { label: "NDA-backed confidentiality", c: "text-orange-400" },
                            { label: "Direct line to surveys2cad@gmail.com", c: "text-violet-400" },
                        ].map((it) => (
                            <div key={it.label} className="flex items-center gap-3 text-sm text-slate-300">
                                <CheckCircle size={18} weight="fill" className={it.c} />
                                {it.label}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="lg:col-span-3">
                    {sent ? (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="tech-card p-8 md:p-10 text-center"
                            data-testid="quick-inquiry-success"
                        >
                            <CheckCircle size={48} weight="fill" className="text-cyan-400 mx-auto mb-4" />
                            <h3 className="font-heading text-2xl md:text-3xl font-bold text-slate-50">
                                Inquiry sent to our team.
                            </h3>
                            <p className="text-slate-400 mt-3">
                                We've logged <span className="text-cyan-300">{form.services_looking_for.length || 1}</span>{" "}
                                service{form.services_looking_for.length === 1 ? "" : "s"} and emailed the team at{" "}
                                <span className="text-cyan-300">surveys2cad@gmail.com</span>. Reply within one business hour.
                            </p>
                            <button
                                type="button"
                                onClick={() => {
                                    setSent(false);
                                    setForm({
                                        name: "",
                                        email: "",
                                        phone: "",
                                        company: "",
                                        services_looking_for: [],
                                        survey_type: "",
                                        need_type: "",
                                        message: "",
                                    });
                                }}
                                className="btn-ghost mt-7"
                                data-testid="quick-inquiry-reset-btn"
                            >
                                Send another
                            </button>
                        </motion.div>
                    ) : (
                        <form
                            onSubmit={submit}
                            className="tech-card p-6 md:p-8"
                            data-testid="quick-inquiry-form"
                        >
                            {/* Services multi-select chips */}
                            <label className="overline mb-3 block">Services you're looking for</label>
                            <div className="flex flex-wrap gap-2">
                                {(services.length
                                    ? services.map((s) => s.name)
                                    : SURVEY_TYPES.slice(0, 12)
                                ).map((name) => {
                                    const active = form.services_looking_for.includes(name);
                                    return (
                                        <button
                                            key={name}
                                            type="button"
                                            onClick={() => toggleService(name)}
                                            className={`px-3 py-1.5 rounded-full text-xs font-mono-alt transition-all border ${
                                                active
                                                    ? "bg-cyan-500/20 border-cyan-400/70 text-cyan-200"
                                                    : "bg-slate-900/60 border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-500"
                                            }`}
                                            data-testid={`inquiry-service-${name}`}
                                        >
                                            {name}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Primary survey type */}
                            <div className="mt-6">
                                <label className="overline mb-2 block">Pick a primary survey type</label>
                                <select
                                    value={form.survey_type}
                                    onChange={(e) => setForm({ ...form, survey_type: e.target.value })}
                                    className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500"
                                    data-testid="inquiry-survey-type"
                                >
                                    <option value="">Choose a survey type…</option>
                                    {SURVEY_TYPES.map((t) => (
                                        <option key={t} value={t}>
                                            {t}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Need type radio cards */}
                            <div className="mt-6">
                                <label className="overline mb-2 block">
                                    How often will you need us?{" "}
                                    <span className="text-orange-400">*</span>
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                                    {NEED_OPTIONS.map((n) => {
                                        const active = form.need_type === n.value;
                                        const Icon = n.icon;
                                        return (
                                            <button
                                                key={n.value}
                                                type="button"
                                                onClick={() => setForm({ ...form, need_type: n.value })}
                                                className={`px-3 py-3 rounded-xl text-left border transition-all flex items-center gap-3 ${
                                                    active
                                                        ? n.activeClasses
                                                        : "border-slate-700 bg-slate-900/60 text-slate-300 hover:border-slate-500"
                                                }`}
                                                data-testid={`inquiry-need-${n.value}`}
                                            >
                                                <Icon
                                                    size={22}
                                                    weight={active ? "fill" : "duotone"}
                                                    className={active ? n.iconActive : "text-slate-400"}
                                                />
                                                <span className="text-sm font-heading font-medium">
                                                    {n.label}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Contact fields */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
                                <Field label="Full name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="inquiry-name" />
                                <Field label="Work email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="inquiry-email" />
                                <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="inquiry-phone" />
                                <Field label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="inquiry-company" />
                            </div>

                            {/* Notes */}
                            <div className="mt-4">
                                <label className="overline mb-2 block">Anything else? (optional)</label>
                                <textarea
                                    rows={3}
                                    value={form.message}
                                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                                    placeholder="Acreage, file format, CAD standards, deadline…"
                                    className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500 resize-none"
                                    data-testid="inquiry-notes"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-primary w-full justify-center mt-6"
                                data-testid="inquiry-submit-btn"
                            >
                                {loading ? "Sending…" : "Send inquiry to our team"}
                                <PaperPlaneRight size={16} weight="bold" />
                            </button>
                            <p className="text-[11px] text-slate-500 font-mono-alt mt-3 text-center">
                                Delivered to surveys2cad@gmail.com · Reply in under 1 business hour
                            </p>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
}

function Field({ label, type = "text", required, value, onChange, testid }) {
    return (
        <div>
            <label className="overline mb-2 block">
                {label}
                {required && <span className="text-orange-400 ml-1">*</span>}
            </label>
            <input
                type={type}
                required={required}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500"
                data-testid={testid}
            />
        </div>
    );
}
